import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  //Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  //Assessment as AssessmentIcon,
  LocalShipping as LocalShippingIcon,
  //TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService } from '../../services/api';


//Agregado para funcionalidades de chofer
//import { useNavigate } from 'react-router-dom';
//import { useAuth } from '../../context/AuthContext';

/**
 * Dashboard con vistas específicas por rol
 * Path: backoffice-web/src/pages/Dashboard/Dashboard.js
 * Última modificación: 2026-01-02 16:35
 * Dev: cpalacios01@gmail.com | +595994648273
 */

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const initDashboard = () => {
      try {
        // Obtener usuario del localStorage
        const userStr = localStorage.getItem('admin_user');  // ← CAMBIADO (era 'user')
        
        if (!userStr) {
          setError('No hay sesión activa. Por favor, inicia sesión nuevamente.');
          setLoading(false);
          return;
        }

        const user = JSON.parse(userStr);
        
        // Validar que tenga role_id
        if (!user.role_id) {
          setError('No se pudo determinar tu rol de usuario. Por favor, inicia sesión nuevamente.');
          setLoading(false);
          return;
        }

        setUserRole(user.role_id);
        fetchDashboardStats();
        
      } catch (err) {
        console.error('Error al inicializar dashboard:', err);
        setError('Error al cargar dashboard. Por favor, recarga la página.');
        setLoading(false);
      }
    };

    initDashboard();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await dashboardService.getStats();
      setStats(response.data);
      
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Error al cargar las estadísticas del dashboard');
      
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  // Componente de carga
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Componente de error
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {error.includes('inicia sesión') && (
          <Alert severity="info">
            Por favor, cierra sesión y vuelve a iniciar sesión para actualizar tus permisos.
          </Alert>
        )}
      </Container>
    );
  }

  // Validar que haya datos
  if (!stats) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          No hay datos disponibles para mostrar.
        </Alert>
      </Container>
    );
  }

  // Renderizar según rol
  if (userRole === 1) {
    return <DashboardAdmin stats={stats} formatCurrency={formatCurrency} />;
  }
  
  if (userRole === 2) {
    return <DashboardConductor stats={stats} formatCurrency={formatCurrency} />;
	//navigate('/chofer/dashboard', { replace: true });
  }
  
  if (userRole === 3) {
    return <DashboardRendidor stats={stats} formatCurrency={formatCurrency} />;
  }

  // Rol no reconocido
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Alert severity="error">
        Rol de usuario no válido. Por favor, contacta al administrador.
      </Alert>
    </Container>
  );
};

// ==========================================
// DASHBOARD ADMINISTRADOR
// ==========================================
const DashboardAdmin = ({ stats, formatCurrency }) => {
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard Administrativo
      </Typography>

      {/* Indicadores principales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Total Usuarios
                  </Typography>
                  <Typography variant="h4">
                    {stats.usuarios?.total || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.usuarios?.activos || 0} activos
                  </Typography>
                </div>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Conductores
                  </Typography>
                  <Typography variant="h4">
                    {stats.usuarios?.conductores || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.usuarios?.rendidores || 0} rendidores
                  </Typography>
                </div>
                <LocalShippingIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Cajas Abiertas
                  </Typography>
                  <Typography variant="h4">
                    {stats.cajas?.abiertas || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.cajas?.cerradas_pendientes || 0} pendientes
                  </Typography>
                </div>
                <AccountBalanceIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Recaudación Hoy
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(stats.recaudacion?.hoy || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.boletas?.hoy || 0} boletas
                  </Typography>
                </div>
                <AttachMoneyIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Indicadores secundarios */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Boletas Este Mes
              </Typography>
              <Typography variant="h5">{stats.boletas?.mes || 0}</Typography>
              <Typography variant="caption" color="textSecondary">
                Total: {stats.boletas?.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recaudación Mes
              </Typography>
              <Typography variant="h6">
                {formatCurrency(stats.recaudacion?.mes || 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total: {formatCurrency(stats.recaudacion?.total || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Tarifas Activas
              </Typography>
              <Typography variant="h5">{stats.tarifas?.activas || 0}</Typography>
              <Typography variant="caption" color="textSecondary">
                Total: {stats.tarifas?.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rendiciones Mes
              </Typography>
              <Typography variant="h5">{stats.rendiciones?.mes || 0}</Typography>
              <Typography variant="caption" color="textSecondary">
                Con diferencia: {stats.rendiciones?.con_diferencia || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficas */}
      <Grid container spacing={3}>
        {/* Recaudación últimos días */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recaudación Últimos 7 Días
            </Typography>
            {stats.recaudacion_ultimos_dias && stats.recaudacion_ultimos_dias.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={stats.recaudacion_ultimos_dias}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#8884d8" name="Recaudación" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No hay datos disponibles
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Boletas por tarifa */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Boletas por Tarifa (Este Mes)
            </Typography>
            {stats.boletas_por_tarifa && stats.boletas_por_tarifa.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.boletas_por_tarifa}
                    dataKey="total_boletas"
                    nameKey="nombre"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {stats.boletas_por_tarifa.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No hay datos disponibles
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Top conductores */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Conductores del Mes
            </Typography>
            {stats.top_conductores && stats.top_conductores.length > 0 ? (
              <List dense>
                {stats.top_conductores.map((conductor) => (
                  <ListItem key={conductor.id}>
                    <ListItemText
                      primary={conductor.nombre}
                      secondary={`${conductor.total_cajas || 0} cajas`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No hay datos disponibles
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Recaudación por tarifa */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recaudación por Tarifa
            </Typography>
            {stats.boletas_por_tarifa && stats.boletas_por_tarifa.length > 0 ? (
              <List dense>
                {stats.boletas_por_tarifa.map((tarifa) => (
                  <ListItem key={tarifa.id}>
                    <ListItemText
                      primary={tarifa.nombre}
                      secondary={`${formatCurrency(tarifa.total_monto || 0)} (${tarifa.total_boletas || 0} boletas)`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No hay datos disponibles
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

// ==========================================
// DASHBOARD CONDUCTOR
// ==========================================
const DashboardConductor = ({ stats, formatCurrency }) => {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mi Dashboard - Conductor
      </Typography>

      {/* Caja actual */}
      {stats.caja_actual ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.lighter' }}>
          <Typography variant="h6" gutterBottom>
            💼 Caja Actual
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="textSecondary">Fecha Apertura</Typography>
              <Typography variant="body1">
                {new Date(stats.caja_actual.fecha_apertura).toLocaleString('es-PY')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="textSecondary">Monto Inicial</Typography>
              <Typography variant="h6">
                {formatCurrency(stats.caja_actual.monto_inicial)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="textSecondary">Total Boletas</Typography>
              <Typography variant="h6">
                {stats.caja_actual.total_boletas || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography color="textSecondary">Total Recaudado</Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(stats.caja_actual.total_recaudado)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          No tienes caja abierta actualmente
        </Alert>
      )}

      {/* Indicadores */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cajas Este Mes
              </Typography>
              <Typography variant="h4">{stats.cajas?.mes || 0}</Typography>
              <Typography variant="caption" color="textSecondary">
                Total: {stats.cajas?.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Boletas Este Mes
              </Typography>
              <Typography variant="h4">{stats.boletas?.mes || 0}</Typography>
              <Typography variant="caption" color="textSecondary">
                Total: {stats.boletas?.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recaudación Mes
              </Typography>
              <Typography variant="h5">
                {formatCurrency(stats.recaudacion?.mes || 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total: {formatCurrency(stats.recaudacion?.total || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Promedio por Caja
              </Typography>
              <Typography variant="h5">
                {formatCurrency(
                  stats.cajas?.total > 0
                    ? stats.recaudacion?.total / stats.cajas?.total
                    : 0
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Últimas cajas */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Últimas Cajas
        </Typography>
        {stats.ultimas_cajas && stats.ultimas_cajas.length > 0 ? (
          <List>
            {stats.ultimas_cajas.map((caja) => (
              <ListItem key={caja.id}>
                <ListItemText
                  primary={`Caja #${caja.id} - ${caja.estado}`}
                  secondary={`Apertura: ${new Date(caja.fecha_apertura).toLocaleString('es-PY')} - Inicial: ${formatCurrency(caja.monto_inicial)}`}
                />
                <Chip
                  label={caja.estado}
                  color={caja.estado === 'abierta' ? 'success' : 'default'}
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No tienes cajas registradas
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

// ==========================================
// DASHBOARD RENDIDOR
// ==========================================
const DashboardRendidor = ({ stats, formatCurrency }) => {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard - Rendidor
      </Typography>

      {/* Alertas */}
      {stats.cajas_pendientes > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          Tienes {stats.cajas_pendientes} caja(s) pendiente(s) de rendición
        </Alert>
      )}

      {/* Indicadores */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rendiciones Hoy
              </Typography>
              <Typography variant="h4">{stats.rendiciones?.hoy || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rendiciones Este Mes
              </Typography>
              <Typography variant="h4">{stats.rendiciones?.mes || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'warning.lighter' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cajas Pendientes
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.cajas_pendientes || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Rendiciones
              </Typography>
              <Typography variant="h3">{stats.rendiciones?.total || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Últimas rendiciones */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Últimas Rendiciones
        </Typography>
        {stats.ultimas_rendiciones && stats.ultimas_rendiciones.length > 0 ? (
          <List>
            {stats.ultimas_rendiciones.map((rendicion) => (
              <ListItem key={rendicion.id}>
                <ListItemText
                  primary={`Conductor: ${rendicion.caja?.user?.nombre || 'N/A'}`}
                  secondary={`Fecha: ${new Date(rendicion.fecha_rendicion).toLocaleString('es-PY')}`}
                />
                <Chip
                  label={rendicion.diferencia === 0 ? 'Exacto' : `Diferencia: ${formatCurrency(rendicion.diferencia)}`}
                  color={rendicion.diferencia === 0 ? 'success' : 'error'}
                  size="small"
                  icon={rendicion.diferencia === 0 ? <CheckCircleIcon /> : <WarningIcon />}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No hay rendiciones registradas
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default Dashboard;

// cpalacios01@gmail.com | +595994648273
